'use client';

import { type RefObject, useEffect, useLayoutEffect, useSyncExternalStore } from 'react';

import type { SeoResolveData } from '@/features/seo-listing/types';
import type { FilterState } from '../store';
import type { ListingRestoreSnapshot } from '../types';
import {
  applyListingRestoreChrome,
  handleListingRestoreCaptureClick,
  readListingRestoreSnapshotOnClient,
} from '../utils/listing-restore.util';

interface UseListingRestoreOnBackParams {
  basePath: string;
  listingRestoreSnapshot: ListingRestoreSnapshot | null;
  filterStateRef: RefObject<FilterState>;
  seoPageTitleRef: RefObject<string | null>;
  seoPageRef: RefObject<SeoResolveData | null>;
}

type ListingRestoreCacheEntry = {
  basePath: string;
  snapshot: ListingRestoreSnapshot | null;
};

/** Module cache so getSnapshot returns a stable reference (useSyncExternalStore requirement). */
let listingRestoreCache: ListingRestoreCacheEntry | null = null;

const subscribeListingRestoreStore = (onStoreChange: () => void): (() => void) => {
  window.addEventListener('storage', onStoreChange);
  return () => {
    window.removeEventListener('storage', onStoreChange);
  };
};

const getListingRestoreServerSnapshot = (): ListingRestoreSnapshot | null => null;

const isSameListingRestoreSnapshot = (
  left: ListingRestoreSnapshot | null,
  right: ListingRestoreSnapshot | null,
): boolean => {
  if (left === right) return true;
  if (!left || !right) return false;
  return left.restoreId === right.restoreId && left.href === right.href;
};

const getListingRestoreClientSnapshot = (basePath: string): ListingRestoreSnapshot | null => {
  const nextSnapshot = readListingRestoreSnapshotOnClient(basePath);

  if (
    listingRestoreCache &&
    listingRestoreCache.basePath === basePath &&
    isSameListingRestoreSnapshot(listingRestoreCache.snapshot, nextSnapshot)
  ) {
    return listingRestoreCache.snapshot;
  }

  listingRestoreCache = { basePath, snapshot: nextSnapshot };
  return nextSnapshot;
};

/**
 * Reads the Back-restore snapshot from sessionStorage without hydrating
 * differently from SSR: getServerSnapshot is always null, then the client
 * snapshot is applied after hydration (useSyncExternalStore).
 * getSnapshot must return a cached reference — new objects each call cause
 * "Maximum update depth exceeded".
 */
export const useListingRestoreSnapshot = (basePath: string): ListingRestoreSnapshot | null => {
  return useSyncExternalStore(
    subscribeListingRestoreStore,
    () => getListingRestoreClientSnapshot(basePath),
    getListingRestoreServerSnapshot,
  );
};

/**
 * Listing SEO URLs are applied with history.pushState, so Next.js still
 * treats the catalog as /fanfics. Back from a work page restores that
 * unfiltered RSC tree. Filter state is applied after mount from the snapshot;
 * this hook only syncs the browser chrome and persists the next leave.
 *
 * Do not clear sessionStorage in the null-snapshot branch eagerly: Soft Nav
 * remounts can re-read the snapshot on the second mount. Clearing happens in
 * applyListingRestoreChrome after a successful match.
 */
export const useListingRestoreOnBack = ({
  basePath,
  listingRestoreSnapshot,
  filterStateRef,
  seoPageTitleRef,
  seoPageRef,
}: UseListingRestoreOnBackParams): void => {
  useLayoutEffect(() => {
    if (!listingRestoreSnapshot) {
      return;
    }

    applyListingRestoreChrome(listingRestoreSnapshot);
  }, [listingRestoreSnapshot]);

  useEffect(() => {
    const handleCaptureClick = (event: MouseEvent): void => {
      handleListingRestoreCaptureClick(
        event,
        basePath,
        filterStateRef.current,
        seoPageTitleRef.current,
        seoPageRef.current,
      );
    };

    document.addEventListener('click', handleCaptureClick, true);
    return () => {
      document.removeEventListener('click', handleCaptureClick, true);
    };
  }, [basePath, filterStateRef, seoPageRef, seoPageTitleRef]);
};
